import { useEffect } from 'react'

import { useBoundStore } from '../store/store'
import {
  startSportsMemberToken,
  stopSportsMemberToken,
  supportsSportsToken,
} from '../utils/sports-member-token'

// How long to keep looking for the native bridge, and how often.
//
// The app injects it at document start, so it is almost always there before React mounts — but
// "almost always" is not a guarantee, and missing it means the member browses the whole session on
// a guest ticket with no favourites and no bet slip. A handful of cheap checks closes that window;
// in a browser they simply run out and nothing happens.
const BRIDGE_POLL_MS = 500
const BRIDGE_ATTEMPTS = 6

// Keeps the lite app supplied with a FunSports member token for as long as someone is signed in.
//
// Without it the app browses on a guest ticket, which is 403 on favourites and 401 on the bet
// slip, balance and order history. Inert in a browser: with no native bridge there is nobody to
// push to and no request is made.
const useSportsMemberToken = () => {
  const username = useBoundStore(state => state.user.username)
  const isUserLoading = useBoundStore(state => state.user.isLoading)

  useEffect(() => {
    // Mid-restore: the store has not decided yet, and clearing here would drop a good token on
    // every page load.
    if (isUserLoading) return

    if (!username) {
      stopSportsMemberToken()
      return
    }

    let cancelled = false
    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | undefined

    const attach = () => {
      if (cancelled) return
      if (supportsSportsToken()) {
        void startSportsMemberToken()
        return
      }
      attempts += 1
      if (attempts >= BRIDGE_ATTEMPTS) return
      timer = setTimeout(attach, BRIDGE_POLL_MS)
    }

    attach()

    return () => {
      cancelled = true
      if (timer !== undefined) clearTimeout(timer)
      stopSportsMemberToken()
    }
  }, [username, isUserLoading])
}

export default useSportsMemberToken
